// src/Pages/Invoice/InvoiceTable.jsx
import { useEffect, useState, useRef, useMemo } from "react";
import DataTable from "react-data-table-component";
import { useAuth } from "../Auth/AuthContext";
import InvoiceApiService from "../../ApiServices/InvoiceApiService/InvoiceApiService";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Download, ChevronDown, Check, X, Plus, Trash2 } from "lucide-react";
import { toast } from "react-hot-toast";
import Datepicker from "react-tailwindcss-datepicker";
import InvoiceForm from "./InvoiceForm";
import AppSettings from "../../config/appsettings";

const InvoiceTable = () => {
    const { authState } = useAuth();
    const isAdmin = authState.roles === "Admin";
    const user = authState.user;
    const baseUrl = AppSettings.getBaseUrl(); // TODO: Use appsettings

    const [invoices, setInvoices] = useState([]);
    const [maintainanceMode, setMaintainanceMode] = useState(false);
    const [loading, setLoading] = useState(true);
    const [downloadingId, setDownloadingId] = useState(null);
    const [updatingStatus, setUpdatingStatus] = useState(null);
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const dropdownRefs = useRef({});
    const [dateRange, setDateRange] = useState({
        startDate: null,
        endDate: null,
    });
    const [searchStatus, setSearchStatus] = useState("");
    const [selectedPublisher, setSelectedPublisher] = useState("");

    const [previewUrl, setPreviewUrl] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [showInvoiceForm, setShowInvoiceForm] = useState(false);

    const uniquePublishers = useMemo(() => {
        if (!isAdmin) return [];
        return [...new Set(
            invoices
                .map(inv => inv.userName)
                .filter(Boolean)
        )];
    }, [invoices, isAdmin]);

    useEffect(() => {
        const fetchInvoices = async () => {
            try {
                if (!maintainanceMode) {
                const response = isAdmin
                    ? await InvoiceApiService.getAllInvoices()
                    : await InvoiceApiService.getInvoicesByUserId(user.id);
                setInvoices(response.data);
                }
            } catch (error) {
                console.error("Failed to fetch invoices", error);
                toast.error("Failed to load invoices");
            } finally {
                setLoading(false);
            }
        };
        fetchInvoices();
    }, [isAdmin, user.id]);

    const filteredInvoices = useMemo(() => {
        let result = invoices;

        // Date filter
        if (dateRange.startDate && dateRange.endDate) {
            const start = new Date(dateRange.startDate);
            start.setDate(start.getDate() - 1); // subtract 1 day

            const end = new Date(dateRange.endDate);
            end.setDate(end.getDate() + 1);     // add 1 day
            end.setHours(23, 59, 59, 999);       // ensure full day included

            result = result.filter(invoice => {
                const invoiceDate = new Date(invoice.createdDate);
                return invoiceDate >= start && invoiceDate <= end;
            });
        }


        // Publisher filter
        if (isAdmin && selectedPublisher) {
            result = result.filter(invoice => {
                return invoice.userName === selectedPublisher;
            });
        }

        // Status filter
        if (searchStatus) {
            result = result.filter(invoice =>
                invoice.status?.toLowerCase() === searchStatus.toLowerCase()
            );
        }

        return result;
    }, [invoices, dateRange, searchStatus, selectedPublisher, isAdmin]);

    const handleFilterChange = (setter, value) => {
        setLoading(true);
        setTimeout(() => {
            setter(value);
            setLoading(false);
        }, 400);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openDropdownId && dropdownRefs.current[openDropdownId] &&
                !dropdownRefs.current[openDropdownId].contains(event.target)) {
                setOpenDropdownId(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openDropdownId]);
    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    const handleDownload = async (id, invoiceNumber) => {
        setDownloadingId(id);
        try {
            const url = `${baseUrl}Invoice/download?id=${id}`;
            const response = await fetch(url);
            if (!response.ok) throw new Error('Download failed');
            const blob = await response.blob();
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = invoiceNumber ? `${invoiceNumber}.pdf` : `invoice_${id}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success("Download started");
        } catch (error) {
            console.error("Download failed", error);
            toast.error("Download failed");
        } finally {
            setDownloadingId(null);
        }
    };

    const handleStatusUpdate = async (invoiceId, newStatus) => {
        setUpdatingStatus(invoiceId);
        try {
            await InvoiceApiService.updateInvoiceStatus(invoiceId, newStatus);
            setInvoices(invoices.map(invoice =>
                invoice.id === invoiceId ? { ...invoice, status: newStatus } : invoice
            ));
            toast.success(`Status updated to ${newStatus}`);
            setOpenDropdownId(null);
        } catch (error) {
            console.error("Status update failed", error);
            toast.error("Failed to update status");
        } finally {
            setUpdatingStatus(null);
        }
    };

    const toggleDropdown = (id, event) => {
        event.stopPropagation();
        setOpenDropdownId(openDropdownId === id ? null : id);
    };

    const handlePreview = async (id) => {
        try {
            const url = `${baseUrl}Invoice/download?id=${id}`;
            const response = await fetch(url);
            if (!response.ok) throw new Error('Preview failed');
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            setPreviewUrl(blobUrl);
            setShowPreviewModal(true);
        } catch (error) {
            console.error("Preview failed", error);
            toast.error("Failed to preview PDF");
        }
    };

    const confirmDownload = (invoiceNumber, id) => {
        setDownloadingId(id);
        try {
            const link = document.createElement("a");
            link.href = previewUrl;
            link.download = invoiceNumber ? `${invoiceNumber}.pdf` : `invoice_${id}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success("Download started");
        } catch (error) {
            console.error("Download failed", error);
            toast.error("Download failed");
        } finally {
            setShowPreviewModal(false);
            setDownloadingId(null);
        }
    };

    const handleCreateInvoice = () => {
        setShowInvoiceForm(true);
    };

    const handleDeleteInvoice = async (invoiceId) => {
        if (!window.confirm('Are you sure you want to delete this invoice?')) return;
        
        try {
            await InvoiceApiService.deleteInvoice(invoiceId);
            setInvoices(invoices.filter(inv => inv.id !== invoiceId));
            toast.success('Invoice deleted successfully');
        } catch (error) {
            console.error('Failed to delete invoice', error);
            toast.error('Failed to delete invoice');
        }
    };

    const handleSaveInvoice = async (invoiceData) => {
        try {
            await InvoiceApiService.createInvoice(invoiceData);
            const newInvoices = isAdmin
                ? await InvoiceApiService.getAllInvoices()
                : await InvoiceApiService.getInvoicesByUserId(user.id);
            setInvoices(newInvoices.data);
        } catch (error) {
            throw error;
        }
    };


    const statusOptions = ["Pending", "Sent", "Paid"];
    const statusColors = {
        Pending: "bg-yellow-100 text-yellow-700",
        Sent: "bg-blue-100 text-blue-700",
        Paid: "bg-green-100 text-green-700"
    };

    const columns = useMemo(() => [
        isAdmin && {
            name: "Publisher",
            selector: row => row.userName,
            sortable: true,
            cell: row => (
                <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center mr-2">
                        <span className="text-indigo-600 text-xs font-bold">
                            {row.userName?.charAt(0).toUpperCase()}
                        </span>
                    </div>
                    <span>{row.userName}</span>
                </div>
            )
        },
        {
            name: "Invoice #",
            selector: row => row.invoiceNumber,
            sortable: true,
            cell: row => (
                <span className="font-mono font-bold text-gray-700">
                    #{row.invoiceNumber}
                </span>
            )
        },
        {
            name: "Period",
            cell: row => (
                <div className="flex flex-col">
                    <span className="text-sm font-medium">{row.monthYear}</span>
                    <span className="text-xs text-gray-400">{new Date(row.createdDate).toLocaleDateString()}</span>
                </div>
            )
        },
        {
            name: "Total ($)",
            selector: row => row.invoiceAmount.toFixed(2),
            sortable: true,
            cell: row => (
                <span className="font-bold text-indigo-700">
                    ${row.invoiceAmount.toFixed(2)}
                </span>
            )
        },
        {
            name: "Status",
            cell: row => {
                if (isAdmin) {
                    return (
                        <div className="relative" ref={el => dropdownRefs.current[row.id] = el}>
                            <button
                                onClick={(e) => toggleDropdown(row.id, e)}
                                className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${statusColors[row.status]} transition-all`}
                                disabled={updatingStatus === row.id}
                            >
                                {updatingStatus === row.id ? (
                                    <Loader2 size={14} className="animate-spin mr-1" />
                                ) : (
                                    <>
                                        {row.status}
                                        <ChevronDown size={14} className={`transition-transform ${openDropdownId === row.id ? 'rotate-180' : ''}`} />
                                    </>
                                )}
                            </button>

                            <AnimatePresence>
                                {openDropdownId === row.id && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                        className="absolute z-[100] mt-1 w-40 bg-white shadow-xl rounded-md py-1 border border-gray-200"
                                        style={{
                                            bottom: '100%',
                                            marginBottom: '0.5rem'
                                        }}
                                    >
                                        <div className="px-2 py-1 text-xs text-gray-500 border-b border-gray-100 flex justify-between items-center">
                                            <span>Update Status</span>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenDropdownId(null);
                                                }}
                                                className="text-gray-400 hover:text-gray-600"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                        {statusOptions.map(option => (
                                            <button
                                                key={option}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleStatusUpdate(row.id, option);
                                                }}
                                                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${row.status === option
                                                    ? 'bg-indigo-50 text-indigo-700'
                                                    : 'hover:bg-gray-50 text-gray-700'
                                                    }`}
                                            >
                                                <span>{option}</span>
                                                {row.status === option && <Check size={14} className="text-indigo-600" />}
                                            </button>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    );
                }
                return (
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[row.status]}`}>
                        {row.status}
                    </span>
                );
            }
        },
        {
            name: "Actions",
            cell: row => (
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => handleDownload(row.id, row.invoiceNumber)}
                        disabled={downloadingId === row.id}
                        className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 transition-colors text-sm disabled:opacity-50"
                        title="Download Invoice"
                    >
                        {downloadingId === row.id ? (
                            <Loader2 size={16} className="animate-spin" />
                        ) : (
                            <Download size={16} />
                        )}
                    </button>
                    {isAdmin && (
                        <button
                            onClick={() => handleDeleteInvoice(row.id)}
                            className="flex items-center gap-1 text-red-600 hover:text-red-800 transition-colors text-sm"
                            title="Delete Invoice"
                        >
                            <Trash2 size={16} />
                        </button>
                    )}
                </div>
            )
        }
    ].filter(Boolean), [isAdmin, downloadingId, updatingStatus, openDropdownId]);

    return (
        maintainanceMode ? (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-sm flex items-center justify-center"
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="bg-white max-w-md w-full mx-4 p-8 rounded-2xl shadow-2xl border border-gray-200 text-center"
                >
                    {/* Invoice Icon */}
                    <div className="mb-4 flex justify-center">
                        <div className="p-4 bg-gradient-to-tr from-indigo-100 to-purple-100 rounded-full shadow-inner">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-12 w-12 text-indigo-600"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M9 14l6-6m-5.5 3.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z"
                                />
                            </svg>
                        </div>
                    </div>

                    {/* Heading */}
                    <h2 className="text-2xl font-bold text-gray-800">Invoicing Under Maintenance</h2>

                    {/* Subtext */}
                    <p className="text-sm text-gray-600 mt-2">
                        Our invoicing system is currently undergoing scheduled maintenance.
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                        We’ll be back shortly. Thank you for your patience!
                    </p>
                </motion.div>
            </motion.div>
        ) : (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mt-10 px-4"
            >

                <div className="max-w-7xl mx-auto">
                    <div className="flex justify-between items-center mb-8">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 shadow-sm">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-6 w-6 text-indigo-600"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M9 14l6-6m-5.5 3.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z"
                                    />
                                </svg>
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-gray-800 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                    {isAdmin ? "Publisher Invoices" : "My Invoices"}
                                </h2>
                                <p className="text-xs text-gray-500 mt-1">
                                    {isAdmin ? "Manage all publisher invoices" : "View your invoice history"}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            {isAdmin && (
                                <button
                                    onClick={handleCreateInvoice}
                                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition shadow-md"
                                >
                                    <Plus size={18} />
                                    Create Invoice
                                </button>
                            )}
                            <div className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full border border-gray-200 shadow-sm">
                                <span className="text-xs font-medium text-gray-500">Displaying</span>
                                <span className="text-sm font-semibold text-indigo-600">{filteredInvoices.length}</span>
                                <span className="text-xs font-medium text-gray-500">of</span>
                                <span className="text-sm font-semibold text-gray-700">{invoices.length}</span>
                                <span className="text-xs font-medium text-gray-500">invoices</span>
                            </div>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        {/* Date Range Picker */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                            <Datepicker
                                value={dateRange}
                                onChange={(newValue) => handleFilterChange(setDateRange, newValue)}
                                inputClassName="w-full px-4 py-2 rounded-xl border border-gray-300 shadow-md focus:ring-2 focus:ring-indigo-500"
                                popoverDirection="down"
                                displayFormat="MMM D, YYYY"
                            />
                        </div>

                        {/* Publisher Filter (Admin only) */}
                        {isAdmin && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Publisher</label>
                                <select
                                    className="w-full px-4 py-2 rounded-xl border border-gray-300 shadow-md focus:ring-2 focus:ring-indigo-500"
                                    value={selectedPublisher}
                                    onChange={(e) => handleFilterChange(setSelectedPublisher, e.target.value)}
                                >
                                    <option value="">All Publishers</option>
                                    {uniquePublishers.map(publisher => (
                                        <option key={publisher} value={publisher}>{publisher}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Status Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <select
                                className="w-full px-4 py-2 rounded-xl border border-gray-300 shadow-md focus:ring-2 focus:ring-indigo-500"
                                value={searchStatus}
                                onChange={(e) => handleFilterChange(setSearchStatus, e.target.value)}
                            >
                                <option value="">All Statuses</option>
                                <option value="Pending">Pending</option>
                                <option value="Sent">Sent</option>
                                <option value="Paid">Paid</option>
                            </select>
                        </div>
                    </div>

                    {/* DataTable */}
                    <div className="rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm">
                        {loading ? (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.3 }}
                                className="bg-white/70 backdrop-blur-md p-8 rounded-2xl flex flex-col items-center"
                            >
                                <div className="w-14 h-14 border-4 border-dashed border-indigo-500 rounded-full animate-spin"></div>
                                <p className="mt-4 text-indigo-800 text-base font-semibold tracking-wide animate-pulse">
                                    Loading invoices...
                                </p>
                            </motion.div>
                        ) : (
                            <DataTable
                                columns={columns}
                                data={filteredInvoices}
                                progressPending={loading}
                                pagination
                                highlightOnHover
                                striped
                                responsive
                                noHeader
                                customStyles={{
                                    headCells: {
                                        style: {
                                            fontWeight: '600',
                                            color: '#4b5563',
                                            paddingLeft: '16px',
                                            paddingRight: '16px',
                                        },
                                    },
                                    cells: {
                                        style: {
                                            paddingLeft: '16px',
                                            paddingRight: '16px',
                                            overflow: 'visible',
                                            position: 'relative',
                                        },
                                    },
                                    rows: {
                                        style: {
                                            '&:not(:last-of-type)': {
                                                borderBottom: '1px solid #e5e7eb',
                                            },
                                            position: 'relative',
                                        },
                                    },
                                }}
                            />
                        )}
                    </div>

                    {/* Enhanced Invoice Summary Section */}
                    {!loading && (
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="mt-10 bg-white shadow-2xl rounded-2xl p-8"
                        >
                            <div className="mb-6">
                                <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                                    📑 Invoice Summary
                                </h3>
                                <p className="text-sm text-gray-500 mt-1">Quick stats from filtered invoices</p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                                {[
                                    {
                                        title: "Total Invoices",
                                        value: filteredInvoices.length,
                                        icon: "🧾",
                                        colors: "from-indigo-500 to-purple-500",
                                    },
                                    {
                                        title: "Total Revenue",
                                        value: `$${filteredInvoices.reduce((acc, inv) => acc + (inv.invoiceAmount || 0), 0).toFixed(2)}`,
                                        icon: "💰",
                                        colors: "from-green-500 to-emerald-500",
                                    },
                                    {
                                        title: "Paid Invoices",
                                        value: filteredInvoices.filter(inv => inv.status?.toLowerCase() === "paid").length,
                                        icon: "✅",
                                        colors: "from-blue-500 to-sky-500",
                                    },
                                    {
                                        title: "Pending Invoices",
                                        value: filteredInvoices.filter(inv => inv.status?.toLowerCase() === "pending").length,
                                        icon: "⏳",
                                        colors: "from-yellow-500 to-orange-400",
                                    }
                                ].map((card, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.4, delay: index * 0.05 }}
                                        className={`bg-gradient-to-br ${card.colors} text-white p-6 rounded-2xl shadow-xl hover:scale-[1.03] transform transition-all duration-300`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="text-3xl">{card.icon}</div>
                                            <div>
                                                <p className="text-sm">{card.title}</p>
                                                <p className="text-2xl font-bold mt-1">{card.value}</p>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </div>
                {/* PDF Preview Modal */}
                {showPreviewModal && previewUrl && (
                    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center px-6 py-8">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                            className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl overflow-hidden relative border border-gray-200"
                        >
                            {/* Header */}
                            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-purple-50">
                                <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                                    🧾 Invoice Preview
                                </h3>
                                <button
                                    onClick={() => setShowPreviewModal(false)}
                                    className="text-gray-500 hover:text-red-600 transition"
                                >
                                    <X size={22} />
                                </button>
                            </div>

                            {/* PDF iframe */}
                            <div className="bg-white">
                                <iframe
                                    src={previewUrl}
                                    title="Invoice PDF Preview"
                                    className="w-full h-[650px] border-none"
                                />
                            </div>

                            {/* Footer buttons */}
                            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
                                <button
                                    onClick={() => setShowPreviewModal(false)}
                                    className="px-5 py-2 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => confirmDownload(previewUrl.split("/").pop(), null)}
                                    className="px-5 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition"
                                >
                                    Download PDF
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}

                {/* Invoice Form Modal */}
                {showInvoiceForm && (
                    <InvoiceForm
                        onClose={() => setShowInvoiceForm(false)}
                        onSave={handleSaveInvoice}
                    />
                )}
            </motion.div>
        )
    );
}
export default InvoiceTable;